import { Copy, RefreshCw, Trash2, Timer, AtSign, Pencil, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";

export default function EmailGenerator({
  email, isLoading, selectedLifetime, setSelectedLifetime,
  lifetimeOptions, allowPermanent, allowCustomAddress,
  customAddress, setCustomAddress, customAddressError, setCustomAddressError,
  showCustomInput, setShowCustomInput,
  generateEmail, copyToClipboard, deleteEmail,
  welcomeText, domains, selectedDomain, setSelectedDomain,
}) {
  const { t } = useI18n();
  const displayDomain = selectedDomain || domains?.[0] || "shadowmail.cc";
  return (
    <div className="col-span-12 lg:col-span-8">
      <div className="email-display border border-zinc-800 bg-zinc-950/80 p-4 sm:p-6 lg:p-8 corner-brackets">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="ml-4 text-zinc-500 font-mono text-xs uppercase tracking-widest">
            {email?.lifetime === "permanent" ? t.permAddress : t.tempAddress}
          </span>
        </div>

        {email ? (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-cyan-400 glow-text break-all"
                data-testid="email-address"
              >
                {email.full_address}
              </h1>
              <span className="cursor-blink text-cyan-400 text-4xl">|</span>
            </div>

            {/* Inline custom address input */}
            {allowCustomAddress && showCustomInput && (
              <div className="space-y-1">
                <div className="flex items-center gap-0">
                  <div className="relative flex-1">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={customAddress}
                      onChange={(e) => { setCustomAddress(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '')); setCustomAddressError(""); }}
                      placeholder={t.customPlaceholder}
                      autoFocus
                      className="pl-10 rounded-none border border-zinc-700 bg-transparent text-cyan-400 font-mono text-sm placeholder:text-zinc-600 h-9"
                      data-testid="custom-address-input"
                      onKeyDown={(e) => { if (e.key === 'Enter' && customAddress.trim()) generateEmail(selectedLifetime); }}
                    />
                  </div>
                  <span className="px-3 py-1.5 border border-l-0 border-zinc-700 bg-zinc-900 text-zinc-500 font-mono text-sm whitespace-nowrap h-9 flex items-center">@{displayDomain}</span>
                  <Button
                    onClick={() => { setShowCustomInput(false); setCustomAddress(""); setCustomAddressError(""); }}
                    variant="ghost"
                    size="sm"
                    className="ml-1 text-zinc-500 hover:text-red-400 h-9 w-9 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {customAddressError && (
                  <p className="text-red-400 text-xs font-mono" data-testid="custom-address-error">{customAddressError}</p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button
                onClick={copyToClipboard}
                className="btn-cyber rounded-none border border-cyan-500 bg-transparent text-cyan-400 hover:bg-cyan-500 hover:text-black font-mono uppercase tracking-widest text-xs sm:text-sm transition-colors duration-200"
                data-testid="copy-button"
              >
                <Copy className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t.copy}</span>
              </Button>

              <LifetimeSelect
                value={selectedLifetime}
                onChange={setSelectedLifetime}
                options={lifetimeOptions}
                allowPermanent={allowPermanent}
              />

              {domains && domains.length > 1 && (
                <DomainSelect value={selectedDomain} onChange={setSelectedDomain} domains={domains} />
              )}

              <Button
                onClick={() => generateEmail(selectedLifetime)}
                disabled={isLoading}
                className="btn-cyber rounded-none border border-green-500 bg-transparent text-green-400 hover:bg-green-500 hover:text-black font-mono uppercase tracking-widest text-xs sm:text-sm transition-colors duration-200 group/refresh"
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 sm:mr-2 transition-transform duration-500 ${isLoading ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
                <span className="hidden sm:inline">{t.newEmail}</span>
              </Button>
              <Button
                onClick={deleteEmail}
                className="btn-cyber rounded-none border border-red-500 bg-transparent text-red-400 hover:bg-red-500 hover:text-black font-mono uppercase tracking-widest text-xs sm:text-sm transition-colors duration-200"
                data-testid="delete-button"
              >
                <Trash2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t.delete}</span>
              </Button>
              {allowCustomAddress && !showCustomInput && (
                <Button
                  onClick={() => setShowCustomInput(true)}
                  className="btn-cyber rounded-none border border-amber-500 bg-transparent text-amber-400 hover:bg-amber-500 hover:text-black font-mono text-sm transition-colors duration-200 px-3"
                  title={t.customizeAddress}
                  data-testid="toggle-custom-input-btn"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            {/* Custom Address Input */}
            {allowCustomAddress && (
              <div className="w-full max-w-md space-y-1">
                <label className="text-zinc-500 font-mono text-xs uppercase tracking-widest">{t.customLabel}</label>
                <div className="flex items-center gap-0">
                  <div className="relative flex-1">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={customAddress}
                      onChange={(e) => { setCustomAddress(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '')); setCustomAddressError(""); }}
                      placeholder={t.customPlaceholder}
                      className="pl-10 rounded-none border border-zinc-700 bg-transparent text-cyan-400 font-mono text-sm placeholder:text-zinc-600"
                      data-testid="custom-address-input"
                    />
                  </div>
                  <span className="px-3 py-2 border border-l-0 border-zinc-700 bg-zinc-900 text-zinc-500 font-mono text-sm">@{displayDomain}</span>
                </div>
                {customAddressError && (
                  <p className="text-red-400 text-xs font-mono" data-testid="custom-address-error">{customAddressError}</p>
                )}
              </div>
            )}
            {welcomeText && (
              <p className="text-zinc-400 font-mono text-sm text-center max-w-md">{welcomeText}</p>
            )}
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="text-zinc-500 font-mono text-sm">{t.duration}</span>
              <LifetimeSelect
                value={selectedLifetime}
                onChange={setSelectedLifetime}
                options={lifetimeOptions}
                allowPermanent={allowPermanent}
                testId="lifetime-select-initial"
              />
              {domains && domains.length > 1 && (
                <>
                  <span className="text-zinc-500 font-mono text-sm">{t.domain}</span>
                  <DomainSelect value={selectedDomain} onChange={setSelectedDomain} domains={domains} />
                </>
              )}
            </div>
            <Button
              onClick={() => generateEmail(selectedLifetime)}
              disabled={isLoading}
              className="btn-cyber rounded-none border-2 border-cyan-500 bg-transparent text-cyan-400 hover:bg-cyan-500 hover:text-black font-mono uppercase tracking-widest px-6 py-5 sm:px-8 sm:py-6 text-sm sm:text-lg transition-colors duration-200 w-full sm:w-auto group/refresh"
              data-testid="generate-button"
            >
              <RefreshCw className={`w-5 h-5 mr-3 transition-transform duration-500 ${isLoading ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
              {t.generateAddress}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function LifetimeSelect({ value, onChange, options, allowPermanent, testId = "lifetime-select" }) {
  const { t } = useI18n();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="w-[140px] rounded-none border border-zinc-700 bg-transparent text-zinc-300 font-mono text-sm"
        data-testid={testId}
      >
        <Timer className="w-4 h-4 mr-2 text-cyan-400" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {Object.entries(options).map(([key, opt]) => (
          <SelectItem key={key} value={key} className="font-mono text-sm" data-testid={`lifetime-${key}`}>{opt.label}</SelectItem>
        ))}
        {allowPermanent && (
          <SelectItem value="permanent" className="font-mono text-sm text-amber-400" data-testid="lifetime-permanent">{t.lifetimePermanent}</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function DomainSelect({ value, onChange, domains }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[180px] rounded-none border border-zinc-700 bg-transparent text-zinc-300 font-mono text-sm">
        <Globe className="w-4 h-4 mr-2 text-cyan-400" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {domains.map((d) => (
          <SelectItem key={d} value={d} className="font-mono text-sm">@{d}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
